import { billingStatusAtom, subscriptionAtom, subscriptionFeaturesAtom } from '@/atoms/subscription.atom';
import { execute } from '@/graphql/execute';
import {
  BillingInterval,
  CollectionMethod,
  SubscriptionDetailsQuery,
  SubscriptionInfo,
  SubscriptionTier,
} from '@/graphql/graphql';
import {
  applySubscriptionDiscountMutation,
  cancelSubscriptionMutation,
  changeSubscriptionTierMutation,
  createBillingPortalSessionMutation,
  createSubscriptionMutation,
  reactivateSubscriptionMutation,
  removeSubscriptionDiscountMutation,
  updateSubscriptionStorageMutation,
} from '@/mutations/subscription.mutation';
import { organizationWithSubscriptionQuery, subscriptionDetailsQuery } from '@/queries/subscription.query';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { useOrganization } from './useOrganizations';

export interface CreateSubscriptionParams {
  tier: SubscriptionTier;
  storageBlocks: number;
  billingInterval: BillingInterval;
  collectionMethod: CollectionMethod;
  promoCode?: string;
  successUrl: string;
  cancelUrl: string;
  userEmail?: string;
}

export const useSubscription = () => {
  const organization = useOrganization();
  const organizationId = organization?.id;
  const [subscription, setSubscription] = useAtom(subscriptionAtom);
  const features = useAtomValue(subscriptionFeaturesAtom);
  const billingStatus = useAtomValue(billingStatusAtom);
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<SubscriptionDetailsQuery['subscriptionDetails'] | null>(null);

  // Fetch subscription info for the current organization
  const fetchSubscription = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await execute(organizationWithSubscriptionQuery, { id: organizationId });
      if (data.organization?.subscription) {
        setSubscription(data.organization.subscription as SubscriptionInfo);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch subscription');
    } finally {
      setLoading(false);
      setInitialLoadComplete(true);
    }
  }, [organizationId, setSubscription]);

  // Auto-fetch subscription on mount when organizationId is available
  useEffect(() => {
    if (organizationId && !initialLoadComplete) {
      fetchSubscription();
    }
  }, [organizationId, initialLoadComplete, fetchSubscription]);

  // Fetch detailed subscription info including invoices
  const fetchSubscriptionDetails = useCallback(async () => {
    if (!organizationId) return null;

    setLoading(true);
    setError(null);

    try {
      const data = await execute(subscriptionDetailsQuery, { organizationId });
      setDetails(data.subscriptionDetails);
      if (data.subscriptionDetails?.info) {
        setSubscription(data.subscriptionDetails.info as SubscriptionInfo);
      }
      return data.subscriptionDetails;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch subscription details');
      return null;
    } finally {
      setLoading(false);
    }
  }, [organizationId, setSubscription]);

  // Create a new subscription
  const createSubscription = useCallback(
    async (params: CreateSubscriptionParams) => {
      if (!organizationId) throw new Error('Organization ID is required');

      setLoading(true);
      setError(null);

      try {
        const result = await execute(createSubscriptionMutation, {
          input: {
            organizationId,
            tier: params.tier,
            storageBlocks: params.storageBlocks,
            billingInterval: params.billingInterval,
            collectionMethod: params.collectionMethod,
            promoCode: params.promoCode,
            successUrl: params.successUrl,
            cancelUrl: params.cancelUrl,
            userEmail: params.userEmail,
          },
        });

        return result.createSubscription;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create subscription';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [organizationId],
  );

  // Update storage blocks
  const updateStorage = useCallback(
    async (storageBlocks: number) => {
      if (!organizationId) throw new Error('Organization ID is required');

      setLoading(true);
      setError(null);

      try {
        await execute(updateSubscriptionStorageMutation, {
          input: { organizationId, storageBlocks },
        });
        await fetchSubscription();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update storage';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [organizationId, fetchSubscription],
  );

  // Change subscription tier
  const changeTier = useCallback(
    async (tier: SubscriptionTier) => {
      if (!organizationId) throw new Error('Organization ID is required');

      setLoading(true);
      setError(null);

      try {
        await execute(changeSubscriptionTierMutation, {
          input: { organizationId, tier },
        });
        await fetchSubscription();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to change tier';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [organizationId, fetchSubscription],
  );

  // Cancel subscription
  const cancelSubscription = useCallback(async () => {
    if (!organizationId) throw new Error('Organization ID is required');

    setLoading(true);
    setError(null);

    try {
      await execute(cancelSubscriptionMutation, { organizationId });
      await fetchSubscription();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel subscription';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [organizationId, fetchSubscription]);

  // Reactivate subscription
  const reactivateSubscription = useCallback(async () => {
    if (!organizationId) throw new Error('Organization ID is required');

    setLoading(true);
    setError(null);

    try {
      await execute(reactivateSubscriptionMutation, { organizationId });
      await fetchSubscription();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reactivate subscription';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [organizationId, fetchSubscription]);

  // Apply discount/promo code
  const applyDiscount = useCallback(
    async (promoCode: string) => {
      if (!organizationId) throw new Error('Organization ID is required');

      setLoading(true);
      setError(null);

      try {
        await execute(applySubscriptionDiscountMutation, {
          input: { organizationId, promoCode },
        });
        await fetchSubscriptionDetails();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to apply discount';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [organizationId, fetchSubscriptionDetails],
  );

  // Remove discount
  const removeDiscount = useCallback(async () => {
    if (!organizationId) throw new Error('Organization ID is required');

    setLoading(true);
    setError(null);

    try {
      await execute(removeSubscriptionDiscountMutation, { organizationId });
      await fetchSubscriptionDetails();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove discount';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [organizationId, fetchSubscriptionDetails]);

  // Open billing portal to manage payment methods and billing details
  const openBillingPortal = useCallback(async () => {
    if (!organizationId) throw new Error('Organization ID is required');

    setLoading(true);
    setError(null);

    try {
      const returnUrl = window.location.href;
      const result = await execute(createBillingPortalSessionMutation, {
        organizationId,
        returnUrl,
      });
      // Redirect to Stripe Billing Portal
      window.location.href = result.createBillingPortalSession;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to open billing portal';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  return {
    // State
    subscription,
    features,
    billingStatus,
    details,
    loading,
    initialLoadComplete,
    error,

    // Actions
    fetchSubscription,
    fetchSubscriptionDetails,
    createSubscription,
    updateStorage,
    changeTier,
    cancelSubscription,
    reactivateSubscription,
    applyDiscount,
    removeDiscount,
    openBillingPortal,
  };
};
